import { ColorString, FlightRoutes, PlannedRouteType, RouteWaypointModel } from '@fe/core';
import { Profile, ProfileHoverHandler, ProfileHoverItem, ProfileRouteProjectionMode } from '@fe/flight-profile';
import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';
import { Button } from '@progress/kendo-react-buttons';
import { ToggleButton } from '@progress/kendo-react-dateinputs';
import { Checkbox, ColorPicker, NumericTextBox, Slider } from '@progress/kendo-react-inputs';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Coordinate from '../../../common/coordinate';
import SettingsSubSection from '../../../components/common/SettingsSubSection';
import { ImpactsTooltip } from '../../../components/map-interaction/tooltip/ImpactsTooltip';
import { UnitFormatter } from '../../../formatters';
import useDebugFeatures from '../../../hooks/use-debug-features';
import { CloseOutline, CogOutline, ShareOutline, WindyOutline } from '../../../icons/icons';
import {
    FEServerFlightDataRouteWindAltitudePoint,
    FEServerFlightDataRouteWinds,
    FlightDataService
} from '../../../services/axios/axios-api-v2-generated';
import { useEvent } from '../../contexts/event-bus';
import { MapPaddingContext } from '../../contexts/map-padding-context';
import { useFocusedWorkspaceSelector, useWorkspaceView } from '../../features/home/hooks/use-view';
import { PanelTitleTrailingContent } from '../../features/home/panels/HomePanel';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import useMap from '../../hooks/use-map';
import { useSafeEffect } from '../../hooks/use-safe-effect';
import { patchFocusedWorkspace } from '../../state/store';
import { DropdownListWithValueField } from '../inputs/DropdownListWithValueField';
import SegmentedControl from '../inputs/SegmentedControl';
import CustomPopover from '../layout/CustomPopover';
import { FlightProfileBackground, flightProfileBackgroundOptions } from './types';

// Force all fields as defined. Open api generator generates them all as undefined...
type FeServerFlightDataRouteWindsDefined = Required<Omit<FEServerFlightDataRouteWinds, 'elements'>> & {
    elements: Array<{ dar: number; oar: number; alts: Required<FEServerFlightDataRouteWindAltitudePoint>[] }>;
};

export function FlightProfile() {
    const [error, setError] = React.useState<string>();
    const selectedId = useFocusedWorkspaceSelector((x) => x.flights.selected[0]?.details.id);
    const acid = useFocusedWorkspaceSelector((x) => x.flights.selected[0]?.details.acid);
    const origin = useFocusedWorkspaceSelector((x) => x.flights.selected[0]?.details.origin);
    const departureTime = useFocusedWorkspaceSelector((x) => x.flights.selected[0]?.details.departureTime);
    const arrivalTime = useFocusedWorkspaceSelector((x) => x.flights.selected[0]?.details.arrivalTime);
    const routes = useFocusedWorkspaceSelector((x) => x.flights.selected[0]?.routes) as FlightRoutes | undefined;
    const forward = useFocusedWorkspaceSelector((x) => x.flights.fastForward);
    const routeFlownColors = useWorkspaceView((x) => x.plane.settings.routeFlown);
    const drawFlownProjectedPoints = useWorkspaceView((x) => x.plane.settings.showProjectedPoints);
    const [drawFlownPoints, setDrawFlownPoints] = React.useState(true);
    const [drawDayNightLine, setDrawDayNightLine] = React.useState(true);
    const [drawWindBarbs, setDrawWindBarbs] = React.useState(false);
    const [drawImpacts, setDrawImpacts] = React.useState(true);
    const [drawTerrain, setDrawTerrain] = React.useState(true);
    const [drawEtops, setDrawEtops] = React.useState(true);
    const [routeSource, setPlannedRouteType] = React.useState(PlannedRouteType.atc);
    const [backgroundLightColor, setBackgroundLightColor] = React.useState('rgb(148,148,148)');
    const [dayNightLightColor, setDayNightLightColor] = React.useState('rgba(40,40,40,0.25)');
    const [backgroundDarkColor, setBackgroundDarkColor] = React.useState('rgb(30,30,30)');
    const [dayNightDarkColor, setDayNightDarkColor] = React.useState('rgba(11,11,11,0.25)');
    const [barbWidth, setBarbWidth] = React.useState(24);
    const [barbHeight, setBarbHeight] = React.useState(24);
    const [barbSpacingX, setBarbSpacingX] = React.useState(8);
    const [barbSpacingY, setBarbSpacingY] = React.useState(8);
    const [heatMapOpacity, setHeatMapOpacity] = React.useState(0.25);
    const [background, setBackground] = useState<FlightProfileBackground>(FlightProfileBackground.solid);
    const [horizontalProjection, setHorizontalProjection] = React.useState(ProfileRouteProjectionMode.distanceAlongRoute);
    const [profile, setProfile] = useState<Profile | null>(null);
    const isDark = useAppSelector((x) => x.settings.isDark);
    const [hover, setHover] = useState<{ event: MouseEvent; items: ProfileHoverItem[] } | null>(null);
    const selectedPlannedRoute = routeSource === PlannedRouteType.atc ? routes?.atc : routes?.planned;
    const fobVolumeUnit = useAppSelector((x) => x.settings.flightFobVolumeDisplayUnit);
    const fobWeightUnit = useAppSelector((x) => x.settings.flightFobWeightDisplayUnit);
    const debug = useDebugFeatures();
    const map = useMap();
    const dispatch = useAppDispatch();
    const mapPadding = useContext(MapPaddingContext);
    const winds = useQuery({
        queryFn: async () =>
            await FlightDataService.getFlightRouteWindsAsync(
                {
                    body: {
                        acid: acid!,
                        origin: origin !== null ? origin : undefined,
                        departureTime: departureTime !== null ? dayjs.utc(departureTime * 1000).toISOString() : undefined
                    }
                },
                {
                    withAuthorization: true
                }
            ),
        enabled: (drawWindBarbs || background === FlightProfileBackground.temperature) && !!selectedId && !!acid && !!routes,
        queryKey: [selectedId, acid, routes],
        retry: false
    });

    useSafeEffect(() => {
        const profile = new Profile({ containerId: 'profile-container' });
        setProfile(profile);
        return () => {
            profile.finalize();
        };
    }, []);

    useEffect(() => {
        if (selectedId) {
            if (!profile) {
                return;
            }
            const success = profile.setFlight({
                flightId: selectedId,
                acid: acid ?? '',
                arrivalTime: arrivalTime,
                departureTime: departureTime,
                weather: {
                    winds:
                        winds.data && selectedPlannedRoute
                            ? ((winds.data as FeServerFlightDataRouteWindsDefined[]).find((x) => x.routeId === selectedPlannedRoute.id) ??
                              null)
                            : null
                },
                routes: {
                    planned: selectedPlannedRoute ?? null,
                    flown: routes?.flown ?? null,
                    forward: forward.active && forward.points && forward.flightId === selectedId ? { points: forward.points } : null
                }
            });

            setError(success ? undefined : 'Failed to load flight');
        }
    }, [profile, selectedId, acid, arrivalTime, departureTime, routes?.flown, selectedPlannedRoute, winds.data]);

    React.useEffect(() => {
        profile?.setProps({
            chart: {
                background: {
                    color: (isDark ? backgroundDarkColor : backgroundLightColor) as any
                    //color: backgroundColor as Color
                },
                isDark,
                text: {
                    color: isDark ? '#fff' : '#000',
                    fontName: 'Red Hat Text',
                    baseSize: 10
                },
                margin: {
                    top: 10,
                    right: 10,
                    bottom: 33,
                    left: 10
                }
            },
            horizontalProjection: horizontalProjection,
            plannedRouteType: routeSource,
            fuel: {
                weightUnit: fobWeightUnit as number,
                volumeUnit: fobVolumeUnit as number,
                formatter: UnitFormatter
            },
            drawEtops: drawEtops,
            drawImpacts: drawImpacts,
            drawTerrain: drawTerrain,
            dayNight: {
                color: (isDark ? dayNightDarkColor : dayNightLightColor) as ColorString,
                line: drawDayNightLine,
                shade: background === FlightProfileBackground.dayNight
            },
            routeFlown: {
                drawPoints: drawFlownPoints,
                drawProjected: drawFlownProjectedPoints,
                // prettier-ignore
                colors: {
                    defaultColor: routeFlownColors.defaultColor,
                    projectedColor: routeFlownColors.projected.enabled ? routeFlownColors.projected.color : routeFlownColors.defaultColor,
                    acarsColor: routeFlownColors.acars.enabled ? routeFlownColors.acars.color : routeFlownColors.defaultColor,
                    adsbTerrestrialColor: routeFlownColors.adsbTerrestrial.enabled ? routeFlownColors.adsbTerrestrial.color : routeFlownColors.defaultColor,
                    adsbSatelliteColor: routeFlownColors.adsbSatellite.enabled ? routeFlownColors.adsbSatellite.color : routeFlownColors.defaultColor,
                    faaAnspColor: routeFlownColors.faaAnsp.enabled ? routeFlownColors.faaAnsp.color : routeFlownColors.defaultColor
                }
            },
            barbs: {
                visible: drawWindBarbs,
                size: [barbWidth, barbHeight],
                spacing: [barbSpacingX, barbSpacingY]
            },
            heatmap: {
                visible: background === FlightProfileBackground.temperature,
                opacity: heatMapOpacity
            }
        });
    }, [
        profile,
        isDark,
        background,
        routeSource,
        drawDayNightLine,
        drawFlownProjectedPoints,
        drawFlownPoints,
        routeFlownColors,
        drawWindBarbs,
        horizontalProjection,
        backgroundLightColor,
        backgroundDarkColor,
        dayNightLightColor,
        dayNightDarkColor,
        fobVolumeUnit,
        fobWeightUnit,
        drawImpacts,
        drawTerrain,
        drawEtops,
        barbWidth,
        barbHeight,
        barbSpacingX,
        barbSpacingY,
        heatMapOpacity
    ]);

    const hoverHandler: ProfileHoverHandler = useCallback((e) => {
        setHover((prev) => (e.items.length > 0 ? e : prev ? null : prev));
    }, []);

    const goToWaypointHandler = useCallback(
        (e: RouteWaypointModel) => {
            map?.goToFlight(Coordinate.fromArray(e.position), map.getZoom(), mapPadding);
        },
        [map, mapPadding]
    );

    React.useEffect(() => profile?.setHoverHandler(hoverHandler), [profile, hoverHandler]);
    React.useEffect(() => profile?.setGoToWaypointHandler(goToWaypointHandler), [profile, goToWaypointHandler]);

    const onClose = () => {
        dispatch(patchFocusedWorkspace((x) => (x.flights.settings.showProfile = false)));
    };

    useEvent(
        'fast-forward-progress',
        useCallback(
            (e) => {
                profile?.setFlightForwardPointsCurrentIndex(e);
            },
            [profile]
        )
    );

    useEvent(
        'flight-flown-point-added',
        useCallback(
            (data) => {
                for (const point of data) {
                    if (point.flightId !== selectedId) {
                        continue;
                    }

                    profile?.addFlownPoint(point);
                }
            },
            [selectedId, profile]
        )
    );

    return (
        <div className="d-flex w-100 h-100 flex-column">
            <PanelTitleTrailingContent>
                <div className="d-flex align-items-center">
                    {import.meta.env.DEV && winds.isFetching && <div className="pe-2">Loading winds...</div>}

                    <SegmentedControl
                        className="me-2"
                        value={routeSource}
                        data={routeTypesOptions}
                        style={{ fontSize: '0.70rem', border: '1px solid var(--border-color)' }}
                        onChange={(e) => setPlannedRouteType(e.value)}
                    />

                    <ToggleButton
                        className="button-small rounded-0"
                        title="Draw flown points"
                        selected={drawFlownPoints}
                        togglable={true}
                        fillMode={'flat'}
                        onClick={() => setDrawFlownPoints((p) => !p)}
                        svgIcon={ShareOutline}
                    />

                    <ToggleButton
                        className="button-small rounded-0 d-none"
                        title="Draw wind barbs"
                        selected={drawWindBarbs}
                        togglable={true}
                        fillMode={'flat'}
                        onClick={() => setDrawWindBarbs((p) => !p)}
                        svgIcon={WindyOutline}
                    />

                    <CustomPopover closeOnClick={false} trigger={<Button className="button-small" svgIcon={CogOutline} fillMode="flat" />}>
                        <div className="k-card p-2" style={{ width: 250 }}>
                            <div className="d-flex flex-column gap-2">
                                <div>Background</div>
                                <DropdownListWithValueField
                                    data={flightProfileBackgroundOptions}
                                    value={background}
                                    onChange={(e) => setBackground(e.value)}
                                />
                            </div>
                            <div className="pt-2">
                                <Checkbox checked={drawDayNightLine} label="Day night bar" onChange={(e) => setDrawDayNightLine(e.value)} />
                                <Checkbox checked={drawImpacts} label="Impacts" onChange={(e) => setDrawImpacts(e.value)} />
                                <Checkbox checked={drawEtops} label="ETOPS/EDTO" onChange={(e) => setDrawEtops(e.value)} />
                                <Checkbox checked={drawTerrain} label="Terrain" onChange={(e) => setDrawTerrain(e.value)} />
                            </div>

                            {debug && (
                                <SettingsSubSection title="Debug">
                                    <div className="d-none">
                                        <div>X axis mode</div>
                                        <DropdownListWithValueField
                                            data={[
                                                { value: ProfileRouteProjectionMode.distanceToOrigin, text: 'Distance to origin' },
                                                { value: ProfileRouteProjectionMode.distanceAlongRoute, text: 'Distance along the route' }
                                            ]}
                                            onChange={(e) => setHorizontalProjection(e.value)}
                                            value={horizontalProjection}
                                        />
                                    </div>

                                    <div className="d-flex gap-2 justify-content-start">
                                        <div>
                                            <div>Bg Light</div>
                                            <ColorPicker
                                                views={['gradient']}
                                                value={backgroundLightColor}
                                                onChange={(e) => setBackgroundLightColor(e.value)}
                                            />
                                        </div>

                                        <div>
                                            <div>Bg Dark</div>
                                            <ColorPicker
                                                views={['gradient']}
                                                value={backgroundDarkColor}
                                                onChange={(e) => setBackgroundDarkColor(e.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2 justify-content-start">
                                        <div>
                                            <div>D/N Light</div>
                                            <ColorPicker
                                                views={['gradient']}
                                                value={dayNightLightColor}
                                                onChange={(e) => setDayNightLightColor(e.value)}
                                            />
                                        </div>
                                        <div>
                                            <div>D/N Dark</div>
                                            <ColorPicker
                                                views={['gradient']}
                                                value={dayNightDarkColor}
                                                onChange={(e) => setDayNightDarkColor(e.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-2">
                                        <div className="secondary-text">Wind barbs</div>
                                        <div className="d-flex gap-2">
                                            <div>
                                                <div>Width</div>
                                                <NumericTextBox
                                                    min={1}
                                                    value={barbWidth}
                                                    onChange={(e) => setBarbWidth(e.value as number)}
                                                />
                                            </div>
                                            <div>
                                                <div>Height</div>
                                                <NumericTextBox
                                                    min={1}
                                                    value={barbHeight}
                                                    onChange={(e) => setBarbHeight(e.value as number)}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <div>
                                                <div>Spacing X</div>
                                                <NumericTextBox
                                                    min={0}
                                                    value={barbSpacingX}
                                                    onChange={(e) => setBarbSpacingX(e.value as number)}
                                                />
                                            </div>
                                            <div>
                                                <div>Spacing Y</div>
                                                <NumericTextBox
                                                    min={0}
                                                    value={barbSpacingY}
                                                    onChange={(e) => setBarbSpacingY(e.value as number)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-100">
                                        <div>Heatmap opacity: {heatMapOpacity.toFixed(2)}</div>
                                        <Slider
                                            className="w-100"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={heatMapOpacity}
                                            onChange={(e) => setHeatMapOpacity(+e.value.toPrecision(2))}
                                        />
                                    </div>
                                </SettingsSubSection>
                            )}
                        </div>
                    </CustomPopover>
                    <Button className="button-small" fillMode={'flat'} svgIcon={CloseOutline} onClick={onClose} />
                </div>
            </PanelTitleTrailingContent>
            <div className="flex-grow-1 min-h-0 position-relative">
                <div className="h-100 w-100" id="profile-container"></div>
                {error && (
                    <div
                        style={{ position: 'absolute', inset: 0 }}
                        className="bg-color d-flex justify-content-center align-items-center text-danger">
                        {error}
                    </div>
                )}
                {!selectedPlannedRoute && (
                    <div style={{ position: 'absolute', inset: 0 }} className="bg-color d-flex justify-content-center align-items-center">
                        No route data.
                    </div>
                )}
            </div>
            {hover && hover.items.length > 0 && <ProfileTooltip items={hover.items} event={hover.event} />}
        </div>
    );
}

const routeTypesOptions = [
    { value: PlannedRouteType.planned, label: 'Planned' },
    { value: PlannedRouteType.atc, label: 'ATC' }
];

export function ProfileTooltip(props: { items: ProfileHoverItem[]; event: MouseEvent }) {
    const { refs, floatingStyles } = useFloating({
        open: true,
        whileElementsMounted: autoUpdate,
        placement: 'top',
        middleware: [offset({ mainAxis: 10 }), flip(), shift()]
    });

    const virElement = useMemo(
        () => ({
            getBoundingClientRect: () => {
                return {
                    x: props.event.clientX,
                    y: props.event.clientY,
                    top: props.event.clientY,
                    bottom: props.event.clientY,
                    left: props.event.clientX,
                    right: props.event.clientX,
                    width: 0,
                    height: 0
                };
            }
        }),
        [props.event]
    );

    refs.setReference(virElement);
    React.useEffect(() => {
        refs.setReference(virElement);
    }, [refs, virElement]);
    return (
        <FloatingPortal>
            <div ref={refs.setFloating} style={floatingStyles} className="k-card z-1 pe-none">
                <ImpactsTooltip info={{ kind: 'impacts', items: props.items.map((x) => x.impact) }} />
            </div>
        </FloatingPortal>
    );
}
